/* eslint-disable camelcase */
import React, { useState, useContext } from 'react';
import { useHistory } from 'react-router-dom';
import { useFormik } from 'formik';
import { AxiosError } from 'axios';
import { useIsFetching } from 'react-query';
import { notify } from '@relaypay/uikit';
import useReCaptcha from 'hooks/useReCaptcha';
import Button from '../../components/Button';
import Input from '../../components/InputField';
import { loginFormSchema, LoginFormValuesProps, Token } from './helpers';
import useLogin from '../../services/api/admin/login';
import useToken from '../../hooks/useToken';
import useGetAdminDetails from '../../services/api/admin/getAdminDetails';
import { AdminDetailsContext } from '../../contexts/adminContext';
import { AdminDetails, AdminRole, allowedRoles } from '../../interfaces/admin';
import AppRoute from '../../enums/routes';

const LoginForm: React.FC = () => {
  const history = useHistory();
  const isFetching = useIsFetching();
  const { setToken } = useToken();
  const { setAdminDetails, setIsAdmin } = useContext(AdminDetailsContext);

  const [isAuthorized, setAuthorized] = useState<boolean>(false);

  const { execute, executeV2, RecaptchaV2Component } = useReCaptcha();

  const {
    handleChange,
    validateField,
    values: { email, password },
    errors
  } = useFormik<LoginFormValuesProps>({
    initialValues: {
      email: '',
      password: '',
      recaptchaV3Action: 'admin-panel-login',
      recaptchaV3Token: '',
      recaptchaV2Token: ''
    },
    validationSchema: loginFormSchema,
    validateOnChange: false,
    validateOnBlur: false,
    onSubmit: () => undefined
  });

  const { mutateAsync: login } = useLogin({
    onError: (err: AxiosError) =>
      notify({ type: 'error', message: err.response?.data.message })
  });

  const { refetch: fetchData } = useGetAdminDetails({
    onSuccess: ({
      firstName,
      lastName,
      picture,
      authorities
    }: AdminDetails) => {
      if (allowedRoles.some((element) => authorities.includes(element))) {
        setAuthorized(true);
        notify({ title: 'Success!', message: 'Login authorization success' });
        setTimeout(() => {
          setAdminDetails({
            firstName,
            lastName,
            picture,
            authorities
          });
          if (authorities.includes(AdminRole)) {
            setIsAdmin(true);
          }
          history.push(AppRoute.Dashboard);
        }, 1000);
      } else {
        notify({ type: 'error', message: 'Access denied' });
        setToken('');
      }
    },
    onError: (err: AxiosError) => {
      setToken('');
      notify({ type: 'error', message: err.response?.data.message });
    }
  });

  const formSubmitHandler = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    try {
      const { v3Token } = await execute();

      try {
        const data = await login({
          email,
          password,
          recaptchaV3Token: v3Token,
          recaptchaV3Action: 'login'
        });

        const { access_token } = data as Token;
        setToken(access_token);
        fetchData();
      } catch (loginError) {
        const axiosErr = loginError as AxiosError;
        const status = axiosErr.response?.status;
        const message = axiosErr.response?.data?.message?.toLowerCase() || '';

        const isRecaptchaRejection =
          status === 403 ||
          message.includes('recaptcha') ||
          message.includes('captcha');

        if (isRecaptchaRejection) {
          const v2Token = await executeV2();

          if (!v2Token) {
            notify({ type: 'error', message: 'reCAPTCHA verification failed' });
            return;
          }

          const retryData = await login({
            email,
            password,
            recaptchaV3Token: v3Token,
            recaptchaV3Action: 'login',
            recaptchaV2Token: v2Token
          });

          const { access_token } = retryData as Token;
          setToken(access_token);
          fetchData();
        }
      }
    } catch {
      notify({ type: 'error', message: 'reCAPTCHA verification failed' });
    }
  };

  return (
    <div className="p-6 bg-white w-full md:w-480 m-6">
      <h2 className="mb-6 text-rp-black">Login</h2>
      <form onSubmit={formSubmitHandler}>
        <div className="space-y-6 mb-6">
          <Input
            name="email"
            label="Email address"
            placeholder="xyz@abc.com"
            value={email}
            onChange={handleChange}
            onBlur={() => {
              validateField('email');
              if (password) {
                validateField('password');
              }
            }}
            error={errors.email}
          />
          <Input
            name="password"
            label="Password"
            type="password"
            placeholder="xxxxx"
            value={password}
            onChange={handleChange}
            onBlur={() => validateField('password')}
            error={errors.password}
          />
          <Button
            type="submit"
            primary
            loading={isFetching !== 0}
            disabled={isAuthorized}
          >
            Sign In
          </Button>
        </div>
        {RecaptchaV2Component}
      </form>
    </div>
  );
};

export default LoginForm;
